import { useCallback, useEffect, useRef, useState } from 'react'

import useAnswerChannel from 'state/channels/useAnswerChannel'
import useStore from 'state/knovStore'
import {
    answerHasActiveAiRunSocketWork,
    type AnswerLike,
    shouldKeepAnswerChannelSubscribed,
    TERMINAL_AI_RUN_SOCKET_GRACE_MS,
} from './answerInViewPolicy'

type UseAnswerChannelRetentionOptions = {
    answer: AnswerLike
    isVisible: boolean
}

export default function useAnswerChannelRetention(
    answerId: string,
    { answer, isVisible }: UseAnswerChannelRetentionOptions,
) {
    const { subscribe, unsubscribe } = useAnswerChannel(answerId, {
        autoSubscribe: false,
        reconcileOnSubscribe: true,
        deferReconcileOnSubscribe: true,
    })
    const answerJob = useStore(state => (answerId ? state.answerJobs[answerId] : null))
    const isSubscribedRef = useRef(false)
    const hadActiveAiRunSocketWorkRef = useRef(false)
    const terminalAiRunGraceTimerRef = useRef<number | null>(null)
    const [terminalAiRunGraceUntil, setTerminalAiRunGraceUntil] = useState(0)

    const hasActiveAiRunSocketWork = answerHasActiveAiRunSocketWork({ answer, answerJob })
    const isEnteringTerminalGrace = hadActiveAiRunSocketWorkRef.current && !hasActiveAiRunSocketWork
    const keepChannelSubscribed = shouldKeepAnswerChannelSubscribed({
        isVisible,
        answer,
        answerJob,
        isEnteringTerminalGrace,
        terminalGraceUntil: terminalAiRunGraceUntil,
    })

    const clearTerminalGraceTimer = useCallback(() => {
        if (terminalAiRunGraceTimerRef.current == null) return
        window.clearTimeout(terminalAiRunGraceTimerRef.current)
        terminalAiRunGraceTimerRef.current = null
    }, [])

    const startTerminalGrace = useCallback(() => {
        const graceUntil = Date.now() + TERMINAL_AI_RUN_SOCKET_GRACE_MS
        setTerminalAiRunGraceUntil(graceUntil)
        clearTerminalGraceTimer()
        terminalAiRunGraceTimerRef.current = window.setTimeout(() => {
            setTerminalAiRunGraceUntil(currentGraceUntil =>
                currentGraceUntil === graceUntil ? 0 : currentGraceUntil,
            )
            terminalAiRunGraceTimerRef.current = null
        }, TERMINAL_AI_RUN_SOCKET_GRACE_MS)
    }, [clearTerminalGraceTimer])

    const ensureSubscribed = useCallback(() => {
        if (isSubscribedRef.current) return
        isSubscribedRef.current = true
        subscribe()
    }, [subscribe])

    const unsubscribeIfNeeded = useCallback(() => {
        if (!isSubscribedRef.current) return
        isSubscribedRef.current = false
        unsubscribe()
    }, [unsubscribe])

    useEffect(() => {
        const hadActiveWork = hadActiveAiRunSocketWorkRef.current
        hadActiveAiRunSocketWorkRef.current = hasActiveAiRunSocketWork

        if (!hadActiveWork || hasActiveAiRunSocketWork) return
        startTerminalGrace()
    }, [hasActiveAiRunSocketWork, startTerminalGrace])

    useEffect(() => {
        if (keepChannelSubscribed) {
            ensureSubscribed()
        } else {
            unsubscribeIfNeeded()
        }
    }, [ensureSubscribed, keepChannelSubscribed, unsubscribeIfNeeded])

    useEffect(() => {
        return () => {
            clearTerminalGraceTimer()
            unsubscribeIfNeeded()
        }
    }, [clearTerminalGraceTimer, unsubscribeIfNeeded])

    return { ensureSubscribed }
}
